import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Shield, ShieldCheck, Edit, Eye } from 'lucide-react';

interface RoleSelectorProps {
  userId: string;
  currentRoles: string[];
  onRolesChange: (userId: string, roles: string[]) => Promise<void>;
  disabled?: boolean;
}

const AVAILABLE_ROLES = [
  { value: 'super_admin', label: 'Super Admin', icon: ShieldCheck, color: 'bg-red-100 text-red-800' },
  { value: 'admin', label: 'Admin', icon: Shield, color: 'bg-purple-100 text-purple-800' },
  { value: 'editor', label: 'Editor', icon: Edit, color: 'bg-blue-100 text-blue-800' },
  { value: 'viewer', label: 'Viewer', icon: Eye, color: 'bg-gray-100 text-gray-800' },
];

export const RoleSelector: React.FC<RoleSelectorProps> = ({
  userId,
  currentRoles,
  onRolesChange,
  disabled = false,
}) => {
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>(currentRoles);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSave = async () => {
    await onRolesChange(userId, selectedRoles);
    setIsOpen(false);
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {currentRoles.length === 0 ? (
        <Badge variant="outline" className="text-xs">Sem permissões</Badge>
      ) : (
        currentRoles.map(role => {
          const roleInfo = AVAILABLE_ROLES.find(r => r.value === role);
          if (!roleInfo) return null;
          const Icon = roleInfo.icon;
          return (
            <Badge key={role} className={`text-xs ${roleInfo.color}`}>
              <Icon className="w-3 h-3 mr-1" />
              {roleInfo.label}
            </Badge>
          );
        })
      )}
      
      {!disabled && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
              Editar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-4">
              <h4 className="font-medium text-sm">Atribuir Permissões</h4>
              <div className="space-y-3">
                {AVAILABLE_ROLES.map(role => {
                  const Icon = role.icon;
                  return (
                    <div key={role.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${userId}-${role.value}`}
                        checked={selectedRoles.includes(role.value)}
                        onCheckedChange={() => handleRoleToggle(role.value)}
                      />
                      <Label 
                        htmlFor={`${userId}-${role.value}`}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Icon className="w-4 h-4" />
                        {role.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setSelectedRoles(currentRoles);
                    setIsOpen(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleSave}>
                  Salvar
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
